import { useEffect, useMemo } from 'react';

import { ErrorKind } from '@/core/lib/fetch';
import Actions from '@/core/lib/new-architecture/actions';
import CurrentDomain from '@/core/lib/new-architecture/domain/current.domain';
import Query from '@/core/lib/new-architecture/query';
import Store from '@/core/lib/new-architecture/store';
import type { StoreData } from '@/core/lib/new-architecture/store/store.types';

const useCurrent = (): StoreData<CurrentDomain> & {
  hasNextContactsFollows: boolean;
  fetchNextContactsFollows: () => void;
  hasNextContactsFollowing: boolean;
  fetchNextContactsFollowing: () => void;
} => {
  const { logout } = Actions.auth.useLogout();
  const { requestToken, isLoggedIn, isReady } = Store.auth.useRequest();
  const enabled = !!isLoggedIn && isReady;

  const {
    data,
    isLoading: currentIsLoading,
    isError: currentIsError,
    isSuccess: currentIsSuccess,
    error,
  } = Query.user.useCurrent({
    token: requestToken,
    enabled,
  });

  useEffect(() => {
    if (error?.kind === ErrorKind.Unauthorized && isLoggedIn && !currentIsLoading) {
      logout();
    }
  }, [error, isLoggedIn, currentIsLoading]);

  const search = useMemo(() => {
    if (!data) return undefined;

    const sub = data.subs?.find(c => c.id === data.sub);

    if (sub) {
      return sub.alias;
    }

    const city = data.cities?.find(c => c.id === data.city);

    if (city) {
      return city.alias;
    }

    return undefined;
  }, [data]);

  const { data: loc } = Store.locs.useDetail(search);

  const {
    data: control,
    isLoading: controlIsLoading,
    isError: controlIsError,
    isSuccess: controlIsSuccess,
  } = Query.user.useControl({
    token: requestToken,
    enabled,
  });

  const {
    data: contacts,
    isLoading: contactsIsLoading,
    isError: contactsIsError,
    isSuccess: contactsIsSuccess,
  } = Query.user.useContacts({
    token: requestToken,
    enabled,
  });

  const {
    data: contactsFollows,
    isLoading: contactsFollowsIsLoading,
    isError: contactsFollowsIsError,
    isSuccess: contactsFollowsIsSuccess,
    hasNextPage: hasNextContactsFollows,
    fetchNextPage: fetchNextContactsFollows,
  } = Query.user.useContactsFollows({
    token: requestToken,
    enabled,
  });

  const {
    data: contactsFollowing,
    isLoading: contactsFollowingIsLoading,
    isError: contactsFollowingIsError,
    isSuccess: contactsFollowingIsSuccess,
    hasNextPage: hasNextContactsFollowing,
    fetchNextPage: fetchNextContactsFollowing,
  } = Query.user.useContactsFollowing({
    token: requestToken,
    enabled,
  });

  const queriesAreNotInSuccess = !currentIsSuccess && !controlIsSuccess && !contactsIsSuccess && !contactsFollowsIsSuccess && !contactsFollowingIsSuccess;
  const queriesAreNotInError = !currentIsError && !controlIsError && !contactsIsError && !contactsFollowsIsError && !contactsFollowingIsError;
  const queriesAreLoading = currentIsLoading || controlIsLoading || contactsIsLoading || contactsFollowsIsLoading || contactsFollowingIsLoading;

  const isLoading = (enabled && queriesAreNotInError && queriesAreNotInSuccess) || queriesAreLoading;

  const user = useMemo(() => {
    if (!data) return null;

    return new CurrentDomain({ data, control, contacts, loc, contactsFollowing: contactsFollows, contactsFollowed: contactsFollowing });
  }, [data, control, contacts, loc, contactsFollows, contactsFollowing]);

  return {
    data: user,
    isLoading,
    notFound: false,
    hasNextContactsFollows,
    fetchNextContactsFollows,
    hasNextContactsFollowing,
    fetchNextContactsFollowing,
  };
};

export default { useCurrent };
